import {
  FaultSpan,
  dedupeRealFaults,
  SPAN_POSITION_TOLERANCE,
} from '../health/fault-health.util';
import { LossDataRow } from '../models/loss-data-row';

/** The only part of a loss row the fold reads. */
export type FaultBearingRow = Pick<LossDataRow, 'FD' | 'FP' | 'FM'>;

/**
 * Incremental, bounded fault-span derivation.
 *
 * What this replaces
 * ------------------
 * GraphComponent called `deriveFaultSpans(this.lossDataRows)` on EVERY real-time
 * poll — from both the loss backfill and the loss RT handler — re-folding the
 * entire loss history each time. That is O(ticks) per poll, growing linearly with
 * cure duration, over an array that itself grew without bound.
 *
 * `deriveFaultSpans` is a left-fold over ticks carrying `{ open, closed }` state,
 * so it can be advanced one tick at a time in O(faults-in-this-tick). This class
 * holds that state. The fold body below is a direct transcription of
 * `deriveFaultSpans`' loop and uses the same exported helpers
 * (`dedupeRealFaults`, `SPAN_POSITION_TOLERANCE`) so the two cannot drift on the
 * matching rules; equivalence is pinned by spec against `deriveFaultSpans` itself.
 *
 * Bounding
 * --------
 * `open` is naturally bounded by the number of concurrently-active faults.
 * `closed` grows with the number of distinct fault episodes over a cure, so it is
 * capped, oldest-first. Spans are small fixed-size objects (no per-position
 * arrays), so the cap is generous — it exists to make retention provably bounded
 * over a multi-day cure, not because spans are heavy.
 *
 * Tick indices stay ABSOLUTE, matching TelemetryRingBuffer's addressing, so a span
 * still lines up with the slider after rows are evicted.
 */
export class FaultSpanAccumulator {
  private closed: FaultSpan[] = [];
  private open: FaultSpan[] = [];
  /** Absolute index of the next tick to fold. */
  private next = 0;
  /** Closed spans dropped by the cap. */
  private dropped = 0;

  constructor(readonly maxClosedSpans: number = DEFAULT_MAX_CLOSED_SPANS) {}

  /** Total ticks folded so far. */
  get foldedCount(): number {
    return this.next;
  }
  get openCount(): number {
    return this.open.length;
  }
  get closedCount(): number {
    return this.closed.length;
  }
  get droppedCount(): number {
    return this.dropped;
  }

  /** All spans, closed first then still-open — the shape deriveFaultSpans returns. */
  get spans(): FaultSpan[] {
    return [...this.closed, ...this.open];
  }

  reset(): void {
    this.closed = [];
    this.open = [];
    this.next = 0;
    this.dropped = 0;
  }

  /**
   * Fold one tick. Direct transcription of the deriveFaultSpans loop body.
   */
  foldRow(row: FaultBearingRow | null | undefined): void {
    const t = this.next;
    this.next++;

    if (!row) {
      // deriveFaultSpans `continue`s on a missing row — it does NOT close spans,
      // and still consumes the tick index. Match that exactly.
      return;
    }

    const faults = dedupeRealFaults(row.FD, row.FP, row.FM);
    const extended = new Set<FaultSpan>();

    for (const f of faults) {
      const match = this.open.find(
        (s) =>
          !extended.has(s) &&
          s.FT === f.FT &&
          s.D === f.D &&
          s.endTick === t - 1 &&
          s.PS - SPAN_POSITION_TOLERANCE <= f.PE &&
          f.PS <= s.PE + SPAN_POSITION_TOLERANCE
      );
      if (match) {
        match.endTick = t;
        match.PS = Math.min(match.PS, f.PS);
        match.PE = Math.max(match.PE, f.PE);
        extended.add(match);
      } else {
        const span: FaultSpan = {
          FT: f.FT,
          D: f.D,
          PS: f.PS,
          PE: f.PE,
          startTick: t,
          endTick: t,
          open: true,
        };
        this.open.push(span);
        extended.add(span);
      }
    }

    // Spans not observed this tick are repaired as of this tick.
    const stillOpen: FaultSpan[] = [];
    for (const s of this.open) {
      if (s.endTick === t || extended.has(s)) {
        stillOpen.push(s);
      } else {
        s.open = false;
        this.closed.push(s);
      }
    }
    this.open = stillOpen;

    if (this.closed.length > this.maxClosedSpans) {
      const excess = this.closed.length - this.maxClosedSpans;
      this.closed.splice(0, excess);
      this.dropped += excess;
    }
  }

  /** Fold a whole row set from tick 0 (backfill). */
  rebuildFrom(rows: readonly (FaultBearingRow | undefined)[]): void {
    this.reset();
    for (const row of rows) {
      this.foldRow(row);
    }
  }
}

/** Cap on retained closed spans. See the class doc for why this is generous. */
export const DEFAULT_MAX_CLOSED_SPANS = 500;
